import type { CascaderOption } from '@/components';
import { t } from '@/i18n';
import {
  type AmountUnit,
  AmountUnitLabelModifier,
  formatValueByUnit,
} from '@/utils';
import chroma from 'chroma-js';
import type {
  Chart,
  Options,
  Point,
  PointLabelObject,
  SVGAttributes,
  Series,
  SeriesOptionsRegistry,
} from 'highcharts';
import type { z } from 'zod';
import type { ProjectAnalysisModels, graphTypeEnum } from '../_models/project';
import { LABEL_MAX_LENGTH } from './constants';

type ProjectAnalysisType = z.infer<typeof ProjectAnalysisModels>;

type Key = Extract<
  keyof SeriesOptionsRegistry,
  'SeriesLineOptions' | 'SeriesColumnOptions' | 'SeriesAreaOptions'
>;
type SeriesDataType = SeriesOptionsRegistry[Key];

const chromaColorSet3 = [
  '#8dd3c7',
  '#ffffb3',
  '#bebada',
  '#fb8072',
  '#80b1d3',
  '#fdb462',
  '#b3de69',
  '#fccde5',
  '#d9d9d9',
  '#bc80bd',
  '#ccebc5',
  '#ffed6f',
];

const getColorPalette = (dataLength: number) => {
  return chroma.scale(chromaColorSet3).colors(dataLength);
};

type GraphDataType = {
  [organizationId: string]: ProjectAnalysisType;
};

type GetSeriesDataType = {
  data: GraphDataType;
  handlePointClick: (value: { id: string }) => void;
  currentId?: string | null;
};

export const getSeriesData = ({
  data,
  handlePointClick,
  currentId,
}: GetSeriesDataType) => {
  const colorPalette = getColorPalette(Object.keys(data).length);
  return Object.keys(data).map((key, idx) => {
    // キーに一致するIDを持つデータから分析軸情報を取得
    const find = data[key]?.find(
      (item) => item.analysisAxis.id === key,
    )?.analysisAxis;
    return {
      // 分析軸の名前（未設定の場合は「未設定」と表示）
      name: find?.name?.toString() ?? t('not-set'),
      cursor: 'pointer',
      color: colorPalette[idx],
      // カスタムデータ（クリックイベント等で使用）
      custom: {
        id: find?.id,
        parentId: find?.parent,
      },
      // 年度ごとのデータポイント（x: 年度, y: 金額）
      data:
        data[key]?.map((item) => ({
          x: item.fiscalYear,
          y: item.total,
        })) ?? [],
      events: {
        click: function (this: Series) {
          const id = this.options.custom?.id;
          // 現在選択中でないIDがクリックされた場合のみハンドラーを実行
          if (id && id !== currentId) {
            handlePointClick({ id });
          }
        },
      },
    };
  });
};

type GetGraphOptionsArgs = {
  graphType: z.infer<typeof graphTypeEnum>;
  seriesData: Array<Omit<SeriesDataType, 'type'>>;
  data: GraphDataType;
  selectedYear: number | null;
  unit: AmountUnit;
  handlePointClick: (value: { id: string }) => void;
  currentId?: string | null;
};

export const getGraphOptions = ({
  graphType,
  seriesData,
  data,
  selectedYear,
  unit,
  handlePointClick,
  currentId,
}: GetGraphOptionsArgs): Options => {
  const colorPalette = getColorPalette(Object.keys(data).length);

  const formattedData = Object.keys(data).map((key, idx) => {
    const find = data[key]?.find((item) => item.fiscalYear === selectedYear);
    const name =
      data[key]?.find((item) => item.analysisAxis.id === key)?.analysisAxis
        .name ?? key;
    return {
      name,
      y: find?.total,
      color: colorPalette[idx],
      custom: {
        parentId: find?.analysisAxis.parent,
        id: find?.analysisAxis.id,
      },
    };
  });

  switch (graphType) {
    case 'stackedVerticalBar':
      return {
        series: (
          seriesData as Array<SeriesOptionsRegistry['SeriesColumnOptions']>
        ).map((item) => ({
          ...item,
          type: 'column',
          visible: true,
        })),
        plotOptions: {
          column: {
            stacking: 'normal',
            groupPadding: 0,
            groupZPadding: 0,
            borderWidth: 0,
            states: {
              hover: {
                borderWidth: 1,
                borderColor: 'rgb(0, 0, 0, 0.85)',
              },
            },
            events: {
              mouseOver: function () {
                for (const point of this.points) {
                  if (point.visible) {
                    point.setState('hover', true);
                  }
                }
              },
              mouseOut: function () {
                for (const point of this.points) {
                  point.setState('normal');
                }
              },
            },
          },
        },
      };
    case 'treeMap':
      return {
        series: [
          {
            type: 'treemap',
            data: formattedData.map((item) => ({
              ...item,
              value: item.y,
            })),
            events: {
              click: function (this, events) {
                const id = events.point.options.custom?.id;
                if (id && id !== currentId) {
                  handlePointClick({ id });
                }
              },
            },
            visible: true,
          },
        ],
        plotOptions: {
          treemap: {
            borderWidth: 0,
            point: {
              events: {
                mouseOver: function () {
                  if (this.graphic) {
                    this.graphic.attr({
                      'stroke-width': 1,
                      stroke: 'rgb(0, 0, 0, 0.85)',
                    });
                  }
                },
                mouseOut: function () {
                  if (this.graphic) {
                    this.graphic.attr({ 'stroke-width': 0 });
                  }
                },
              },
            },
          },
        },
      };
    case 'pieChart':
      return {
        series: [
          {
            name: 'Pie',
            type: 'pie',
            cursor: 'pointer',
            borderWidth: 0,
            data: formattedData.map((item) => ({
              ...item,
              dataLabels: {
                formatter: function (this: PointLabelObject) {
                  const fullName = this.key ?? '';
                  return `<div style="display: flex; flex-direction: column; font-size: 12px">
                        <span title="${escapeHtml(fullName)}">${escapeHtml(truncateLabel(fullName))}</span>
                        <span>${formatValueByUnit(item.y ?? 0, {
                          operation: 'round',
                          unit,
                          showUnitLabel: true,
                        })} (${this.percentage.toFixed(2)}%)</span>
                      </div>`;
                },
                useHTML: true,
              },
            })),
            visible: true,
            events: {
              click: function (this, events) {
                const id = events.point.options.custom?.id;
                if (id && id !== currentId) {
                  handlePointClick({ id });
                }
              },
            },
          },
        ],
        plotOptions: {
          pie: {
            point: {
              events: {
                mouseOver: function () {
                  if (this.graphic) {
                    this.graphic.attr({
                      'stroke-width': 1,
                      stroke: 'rgb(0, 0, 0, 0.85)',
                    });
                  }
                },
                mouseOut: function () {
                  if (this.graphic) {
                    this.graphic.attr({ 'stroke-width': 0 });
                  }
                },
              },
            },
          },
        },
      };
    default:
      return {};
  }
};

/**
 * グラフのデータラベル用に、名称を `LABEL_MAX_LENGTH` で省略する。
 * 円グラフのラベルが長いとグラフ描画領域が圧迫されるため、省略してホバー（title 属性）で
 * フル名称を表示する用途で使う。
 */
export const truncateLabel = (name: string): string =>
  name.length > LABEL_MAX_LENGTH
    ? `${name.substring(0, LABEL_MAX_LENGTH)}...`
    : name;

/**
 * HTML の本文・属性値に名称（ユーザー入力由来）を埋め込む際のエスケープ。
 * useHTML のデータラベル / ツールチップへ分析軸名を出力する箇所で XSS・表示崩れを防ぐ。
 */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const tooltipText = (unit: AmountUnit, value?: number) => {
  if (value === undefined) {
    return '';
  }
  return formatValueByUnit(value, {
    operation: 'round',
    unit,
    showUnitLabel: true,
  });
};

type GetLineGraphOptionsArgs = {
  graphData: ProjectAnalysisType;
  graphOptions: Options;
  graphType: z.infer<typeof graphTypeEnum>;
  unit: AmountUnit;
};
export const getLineGraphOptions = ({
  graphData,
  graphOptions,
  graphType,
  unit,
}: GetLineGraphOptionsArgs): Options => {
  return {
    chart: {
      backgroundColor: 'transparent',
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    caption: {
      x: 150,
      y: 1,
      verticalAlign: 'top',
      useHTML: true,
      style: {
        fontSize: '12px',
      },
    },
    tooltip: {
      // 円グラフ・ツリーマップは系列名ではなく点ごとの名称が意味を持つためヘッダ（年度）を出さない
      headerFormat:
        graphType === 'treeMap' || graphType === 'pieChart'
          ? ''
          : '<b>{point.x}</b><br/>',
      useHTML: true,
      // 円グラフはデータラベルを省略表示しているため、ホバー時にフル名称を出す。
      // ネイティブ title 属性と異なり Highcharts のツールチップは即時表示できる。
      enabled: true,
      backgroundColor: '#ffffff',
      // useHTML のツールチップが SVG 内に描画されると背後のデータラベルが透けるため、
      // チャート外の独立 DOM（最前面）に描画して白背景を確実に乗せる。
      outside: true,
      style: { fontSize: '14px' },
      pointFormatter: function (this) {
        const label =
          graphType === 'treeMap' || graphType === 'pieChart'
            ? this.name
            : this.series.name;
        return `<div>
          <span style="color: ${this.color};">●</span>
          <span>${escapeHtml(label ?? '')}: </span>
          <b>${tooltipText(unit, this.y)}</b>
        </div>`;
      },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      title: { text: t('fiscal-year') },
      tickInterval: 1,
      categories: [
        ...new Set(graphData.map((item) => item.fiscalYear.toString())),
      ],
      visible: graphType !== 'pieChart',
      labels: {
        style: {
          fontSize: '14px',
          letterSpacing: '-1px',
        },
      },
    },
    yAxis: {
      title: {
        text: t('amount-label-with-unit', {
          unit: AmountUnitLabelModifier[unit],
        }),
      },
      visible: graphType !== 'pieChart',
      labels: {
        formatter: function (this) {
          return formatValueByUnit(Number(this.value), {
            operation: 'round',
            unit,
          });
        },
      },
    },
    ...graphOptions,
  };
};

type GetGraphLabelArgs = {
  point: Point;
  chart: Chart;
  unit: AmountUnit;
  options: {
    labelHTML?: string;
    attributes: SVGAttributes;
  };
  coordinates?: {
    positionX: number;
    positionY: number;
    anchorX: number;
    anchorY: number;
  };
};

export const getGraphLabel = ({
  point,
  chart,
  unit,
  options,
  coordinates = { positionX: 0, positionY: 0, anchorX: 0, anchorY: 0 },
}: GetGraphLabelArgs) => {
  const { positionX, positionY, anchorX, anchorY } = coordinates;
  const { labelHTML, attributes } = options;

  const labelText = truncateLabel(point.series.name);

  return chart.renderer
    .label(
      labelHTML ??
        `<b>${point.x}</b><br/>
        <div>
          <span style="color: ${point.color};">●</span>
          <span>${labelText}</span>
          <br/>
          <b>${tooltipText(unit, point.y)}</b>
        </div>`,
      positionX,
      positionY,
      'callout',
      anchorX,
      anchorY,
      true,
    )
    .attr({
      zIndex: 10,
      padding: 8,
      stroke: point.color,
      strokeOpacity: 0.15,
      ...attributes,
    })
    .css({
      borderRadius: 3,
      fontSize: '14px',
    })
    .add();
};

/**
 * 指定された組織IDの階層に従って、子組織の存在を再帰的にチェックする
 * @param nestedOrganizationIds - チェックする組織IDの配列（階層順）
 * @param organizations - 検索対象の組織ツリー
 * @returns 指定された階層に子組織が存在する場合はtrue、それ以外はfalse
 */
export const checkChildrenRecursively = (
  nestedOrganizationIds: string[],
  organizations: CascaderOption[],
): boolean => {
  const [organizationId, ...restOrganizationIds] = nestedOrganizationIds;

  const currentOrganization = organizations.find(
    (org) => org.value === organizationId,
  );
  if (!currentOrganization || !currentOrganization.children?.length) {
    return false;
  }

  // 子組織が存在する場合はtrueを返す
  if (nestedOrganizationIds.length === 1) {
    return true;
  }

  // 子組織が存在しない場合は、各子組織について再帰的に確認
  return checkChildrenRecursively(
    restOrganizationIds,
    currentOrganization.children,
  );
};
